import confetti, { type Options as ConfettiOptions } from "canvas-confetti";

export type CelebrateIntensity = "sparkle" | "burst" | "shower";

/* ----------------------------------------------------------------------------
 * Intensity presets — calibrated so consumers don't have to tune particle
 * counts. AIKO voice: sparkle is restrained, shower is the "you really did
 * something" treatment.
 *
 * `disableForReducedMotion: true` is canvas-confetti's built-in flag — it's
 * applied per-shot below so reduced-motion users get a clean no-op without
 * us having to hand-roll the matchMedia check.
 *
 * The `shower` preset trims `ticks` to 150 (~1.5s of animation) to align with
 * Stripe's success animation cadence and avoid the long-tail perf cost.
 * (canvas-confetti's `useWorker` option for offscreen-canvas rendering is only
 * supported via `confetti.create()`, not per-call — we'd need a separate
 * worker-backed instance to opt in. Tracked as a future improvement.)
 * ------------------------------------------------------------------------- */

const PRESETS: Record<CelebrateIntensity, ConfettiOptions[]> = {
  sparkle: [
    {
      particleCount: 24,
      spread: 50,
      startVelocity: 28,
      ticks: 120,
      origin: { x: 0.5, y: 0.6 },
      scalar: 0.7,
    },
  ],
  burst: [
    {
      particleCount: 80,
      spread: 70,
      startVelocity: 38,
      ticks: 150,
      origin: { x: 0.5, y: 0.6 },
    },
  ],
  shower: [
    {
      particleCount: 40,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.6 },
      startVelocity: 45,
      ticks: 150,
    },
    {
      particleCount: 40,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.6 },
      startVelocity: 45,
      ticks: 150,
    },
    {
      particleCount: 70,
      spread: 90,
      origin: { x: 0.5, y: 0.45 },
      startVelocity: 50,
      ticks: 150,
    },
  ],
};

export interface CelebrateOptions {
  /** `sparkle` (24 particles) · `burst` (80) · `shower` (~150 across 3 multi-origin shots). Default `burst`. */
  intensity?: CelebrateIntensity;
  /** Override or extend the underlying canvas-confetti options for the shot. */
  options?: ConfettiOptions;
  /** When true, runs even if the user prefers reduced motion. Default false. */
  ignoreReducedMotion?: boolean;
}

/**
 * Fire confetti at the calibrated intensity. Honors `prefers-reduced-motion`
 * via canvas-confetti's built-in `disableForReducedMotion` flag (no-op when
 * the OS pref is set, unless `ignoreReducedMotion: true`). SSR-safe.
 *
 *   import { celebrate } from "@aisquare/ui";
 *   celebrate();                        // default burst (~1.5s)
 *   celebrate({ intensity: "sparkle" }); // restrained
 *   celebrate({ intensity: "shower" }); // big moment
 *
 * The Toaster integration `aiko.celebrate(message)` fires `burst` and shows a
 * toast at the same time.
 */
export function celebrate(options: CelebrateOptions = {}): void {
  if (typeof window === "undefined") return;

  const intensity = options.intensity ?? "burst";
  const shots = PRESETS[intensity];
  const disableForReducedMotion = !options.ignoreReducedMotion;

  for (const shot of shots) {
    confetti({
      ...shot,
      ...options.options,
      disableForReducedMotion,
    });
  }
}
